import type { ApiError } from './http';
import { ApiErrorCodes } from './errors';
import type { ApiErrorCodeLike } from './errors';
import type { SentCopiesApi } from './contract';
import type { MessageSummary, SavedSentCopy, SentCopy, SentSettings } from './types';

/**
 * MOCK: copies of sent mail that are not in Sent yet (#84), for
 * `VITE_USE_MOCKS=true` and tests.
 *
 * A real server fails the APPEND for reasons a mock has none of, so the failure
 * is chosen: {@link MockSentCopies.setFailure} (or `sentCopyFailure` on the
 * mock's options, `VITE_MOCK_SENT_COPY=pending|failed` in the dev server)
 * makes every following send keep its copy instead of filing it. A `pending`
 * copy is due a minute later and the next `run` after that files it; a
 * `failed` one waits for Save, download or Dismiss, as after the server's
 * retries have given up.
 */

const RETRY_MS = 60_000;
const KEEP_MS = 30 * 86_400_000;

export type MockSentCopyFailure = 'pending' | 'failed';

export interface MockSentCopiesDeps {
  delay: (signal?: AbortSignal) => Promise<void>;
  newId: (prefix: string) => string;
  mockError: (
    status: number,
    code: ApiErrorCodeLike,
    message: string,
    details?: Record<string, unknown>,
  ) => ApiError;
  accountExists: (accountId: string) => boolean;
  /** True when the mailbox has a background credential, so retries go on unattended. */
  unattended: (accountId: string) => boolean;
  /** Files the message into the mailbox's Sent; answers the folder id and a UID. */
  file: (message: MessageSummary) => { folderId: string; uid: number };
  failure?: MockSentCopyFailure;
}

export interface MockSentCopies {
  api: SentCopiesApi;
  /** False when the mailbox does not want a copy (the setting is off). */
  saves: (accountId: string) => boolean;
  /**
   * Called by the mock send once SMTP "accepted" the message: keeps it and
   * answers the open copy when a failure is armed, `undefined` otherwise.
   */
  keep: (message: MessageSummary, recipients: string[]) => SentCopy | undefined;
  setFailure: (failure: MockSentCopyFailure | undefined) => void;
}

interface KeptCopy extends SentCopy {
  message: MessageSummary;
}

export function createMockSentCopies(deps: MockSentCopiesDeps): MockSentCopies {
  const { delay, newId, mockError } = deps;
  let failure = deps.failure;
  let kept: KeptCopy[] = [];
  const saveSetting = new Map<string, boolean>();

  const requireAccount = (accountId: string): void => {
    if (!deps.accountExists(accountId)) {
      throw mockError(404, ApiErrorCodes.NotFound, `Account "${accountId}" was not found.`);
    }
  };

  const requireCopy = (accountId: string, copyId: string): KeptCopy => {
    const copy = kept.find((c) => c.id === copyId && c.accountId === accountId);
    if (copy === undefined) {
      throw mockError(404, ApiErrorCodes.NotFound, 'This copy was already saved or dismissed.');
    }
    return copy;
  };

  const publicCopy = (copy: KeptCopy): SentCopy => {
    const { message: _message, ...rest } = copy;
    return { ...rest, to: [...rest.to] };
  };

  const open = (accountId: string): SentCopy[] =>
    kept.filter((c) => c.accountId === accountId).map(publicCopy);

  const fileCopy = (copy: KeptCopy): SavedSentCopy => {
    const { folderId, uid } = deps.file(copy.message);
    kept = kept.filter((c) => c !== copy);
    return { id: copy.id, savedToFolderId: folderId, sentUid: uid, sentMessageId: copy.message.id };
  };

  const settingsOf = (accountId: string): SentSettings => ({
    saveSentCopy: saveSetting.get(accountId) ?? true,
    serverFilesSent: false,
  });

  const api: SentCopiesApi = {
    async list(accountId, signal) {
      await delay(signal);
      requireAccount(accountId);
      return open(accountId);
    },
    async run(accountId) {
      await delay();
      requireAccount(accountId);
      const now = Date.now();
      const saved = kept
        .filter(
          (c) =>
            c.accountId === accountId &&
            c.state === 'pending' &&
            Date.parse(c.nextAttemptAt ?? '') <= now,
        )
        .map(fileCopy);
      return { items: open(accountId), saved };
    },
    async save(accountId, copyId) {
      await delay();
      return fileCopy(requireCopy(accountId, copyId));
    },
    async eml(accountId, copyId) {
      await delay();
      const { message } = requireCopy(accountId, copyId);
      const eml = [
        `From: ${message.from.email}`,
        `To: ${message.to.map((a) => a.email).join(', ')}`,
        `Subject: ${message.subject}`,
        `Date: ${new Date(message.date).toUTCString()}`,
        `Message-ID: <${message.id}@dao-mail.com>`,
        'Content-Type: text/plain; charset=utf-8',
        '',
        message.snippet,
        '',
      ].join('\r\n');
      return new Blob([eml], { type: 'message/rfc822' });
    },
    async dismiss(accountId, copyId) {
      await delay();
      const copy = requireCopy(accountId, copyId);
      kept = kept.filter((c) => c !== copy);
    },
    async settings(accountId, signal) {
      await delay(signal);
      requireAccount(accountId);
      return settingsOf(accountId);
    },
    async updateSettings(accountId, saveSentCopy) {
      await delay();
      requireAccount(accountId);
      saveSetting.set(accountId, saveSentCopy);
      return settingsOf(accountId);
    },
  };

  return {
    api,
    saves: (accountId) => saveSetting.get(accountId) ?? true,
    keep: (message, recipients) => {
      if (failure === undefined) return undefined;
      const now = Date.now();
      const copy: KeptCopy = {
        id: newId('sent-copy'),
        accountId: message.accountId,
        state: failure,
        attempts: failure === 'failed' ? 5 : 1,
        ...(failure === 'pending'
          ? { nextAttemptAt: new Date(now + RETRY_MS).toISOString(), errorCode: 'temporary' as const }
          : {
              errorCode: 'retries_exhausted' as const,
              error: 'The mail server kept refusing the copy.',
            }),
        unattended: deps.unattended(message.accountId),
        subject: message.subject,
        to: [...recipients],
        sentAt: new Date(now).toISOString(),
        expiresAt: new Date(now + KEEP_MS).toISOString(),
        message,
      };
      kept.push(copy);
      return publicCopy(copy);
    },
    setFailure: (next) => {
      failure = next;
    },
  };
}
